import type { AiCrawler, AiCrawlerProvider } from './aiCrawlers';

export type AiCrawlerLineup = {
  readonly providers: readonly AiCrawlerProvider[];
  readonly crawlers: readonly AiCrawler[];
  readonly normalizedToProvider: ReadonlyMap<string, AiCrawlerProvider>;
  // The POST /v3/ai-insights/diagnostics `crawlers` field accepts only these ids.
  readonly providerToDiagnosticsCrawler: ReadonlyMap<AiCrawlerProvider, string>;
  // CDN Analytics crawler category keys, so they double as a CDN Analytics filter.
  readonly categories: readonly string[];
};

const CHATGPT: AiCrawler = {
  provider: 'ChatGPT',
  bots: [
    { name: 'GPTBot', userAgent: 'GPTBot', purpose: 'AI training data' },
    { name: 'ChatGPT-User', userAgent: 'ChatGPT-User', purpose: 'User-triggered page fetches' },
  ],
};

const CLAUDE: AiCrawler = {
  provider: 'Claude',
  bots: [
    { name: 'ClaudeBot', userAgent: 'ClaudeBot', purpose: 'AI training data' },
    { name: 'Claude-User', userAgent: 'Claude-User', purpose: 'Fetching pages on user requests' },
    { name: 'Claude-SearchBot', userAgent: 'Claude-SearchBot', purpose: 'Indexing content for search results' },
  ],
};

const PERPLEXITY: AiCrawler = {
  provider: 'Perplexity',
  bots: [
    { name: 'PerplexityBot', userAgent: 'PerplexityBot', purpose: 'General web crawling' },
    { name: 'Perplexity-User', userAgent: 'Perplexity-User', purpose: 'AI assistant browsing on user queries' },
  ],
};

const GEMINI: AiCrawler = {
  provider: 'Gemini',
  bots: [
    { name: 'Google-Extended', userAgent: 'Google-Extended', purpose: 'AI training data for Gemini and Vertex AI' },
  ],
};

const AMAZON: AiCrawler = {
  provider: 'Amazon',
  bots: [
    {
      name: 'Amazonbot',
      userAgent: 'Amazonbot',
      purpose: 'Improving Amazon services and training Amazon AI models',
    },
  ],
};

// Mirrors rest-api's `resolveCanonicalCrawler`.
const SHARED_NORMALIZED_TO_PROVIDER: ReadonlyArray<[string, AiCrawlerProvider]> = [
  ['gptbot', 'ChatGPT'],
  ['chatgpt', 'ChatGPT'],
  ['claudebot', 'Claude'],
  ['claude-web', 'Claude'],
  ['claude-user', 'Claude'],
  ['anthropic-ai', 'Claude'],
  ['perplexitybot', 'Perplexity'],
  ['perplexity-user', 'Perplexity'],
];

export const GEMINI_LINEUP: AiCrawlerLineup = {
  providers: ['ChatGPT', 'Gemini', 'Claude', 'Perplexity'],
  crawlers: [CHATGPT, GEMINI, CLAUDE, PERPLEXITY],
  normalizedToProvider: new Map([...SHARED_NORMALIZED_TO_PROVIDER, ['google-extended', 'Gemini']]),
  providerToDiagnosticsCrawler: new Map([
    ['ChatGPT', 'GPTBot'],
    ['Gemini', 'Google-Extended'],
    ['Claude', 'ClaudeBot'],
    ['Perplexity', 'PerplexityBot'],
  ]),
  categories: ['chatgpt', 'claude', 'gemini', 'perplexity-ai'],
};

export const AMAZON_LINEUP: AiCrawlerLineup = {
  providers: ['ChatGPT', 'Claude', 'Perplexity', 'Amazon'],
  crawlers: [CHATGPT, CLAUDE, PERPLEXITY, AMAZON],
  normalizedToProvider: new Map([...SHARED_NORMALIZED_TO_PROVIDER, ['amazonbot', 'Amazon']]),
  providerToDiagnosticsCrawler: new Map([
    ['ChatGPT', 'GPTBot'],
    ['Claude', 'ClaudeBot'],
    ['Perplexity', 'PerplexityBot'],
    ['Amazon', 'Amazonbot'],
  ]),
  categories: ['chatgpt', 'claude', 'perplexity-ai', 'amazon'],
};

// For labelling data that outlives the viewer's lineup, e.g. stored diagnostics
// reports. Ordered so each lineup's providers keep their own relative order.
export const ALL_AI_CRAWLERS: readonly AiCrawler[] = [CHATGPT, GEMINI, CLAUDE, PERPLEXITY, AMAZON];

export const ALL_AI_CRAWLER_PROVIDERS: readonly AiCrawlerProvider[] = ALL_AI_CRAWLERS.map(({ provider }) => provider);
